import React, { useState } from 'react';
import { Download, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui';
import { useToast } from '../../../hooks/use-toast';
import { BRAND } from '../../../config/brand';
import './CreateReportModal.css';

export function CreateReportModal({ isOpen, onClose, onCreate }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    year: new Date().getFullYear().toString(),
    type: '',
  });
  const [errors, setErrors] = useState({});
  const [activeAction, setActiveAction] = useState(null);
  const [selectedProcess, setSelectedProcess] = useState('configurator');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear + 5 - i);
  const isProcessing = Boolean(activeAction);
  const isFormComplete = Boolean(formData.name.trim() && formData.year && formData.type);
  const primaryAction = selectedProcess === 'download' ? 'download' : 'configurator';
  const isPrimaryLoading = activeAction === primaryAction;
  const primaryLabel = selectedProcess === 'download'
    ? (activeAction === 'download'
      ? t('reports.hub.createModal.actions.downloading')
      : t('reports.hub.createModal.actions.downloadPack'))
    : (activeAction === 'configurator'
      ? t('reports.hub.createModal.actions.creating')
      : t('reports.hub.createModal.actions.proceed'));
  const primaryIcon = selectedProcess === 'download' ? <Download size={18} /> : <Sparkles size={18} />;

  const reportTypeOptions = [
    {
      value: 'relazione_agronomica_primo_anno',
      label: t('reports.hub.createModal.types.firstYear.label'),
      description: t('reports.hub.createModal.types.firstYear.description'),
      badge: t('reports.hub.createModal.types.firstYear.badge'),
      disabled: false,
    },
    {
      value: 'relazione_agronomica_annuale',
      label: t('reports.hub.createModal.types.annual.label'),
      description: t('reports.hub.createModal.types.annual.description'),
      badge: null,
      disabled: false,
    },
    {
      value: 'plv_drop_report',
      label: t('reports.hub.createModal.types.plvDrop.label'),
      description: t('reports.hub.createModal.types.plvDrop.description'),
      badge: t('reports.hub.createModal.types.plvDrop.badge'),
      disabled: true,
    },
  ];

  const downloadIncludes = [
    t('reports.hub.createModal.downloadIncludes.graphs'),
    t('reports.hub.createModal.downloadIncludes.tables'),
    t('reports.hub.createModal.downloadIncludes.images'),
  ];
  const processOptions = [
    {
      value: 'download',
      title: t('reports.hub.createModal.process.freeTitle'),
      description: t('reports.hub.createModal.process.freeDescription'),
      icon: <Download size={18} />,
      list: downloadIncludes,
      highlight: false,
    },
    {
      value: 'configurator',
      title: t('reports.hub.createModal.process.paidTitle'),
      description: t('reports.hub.createModal.process.paidDescription'),
      icon: <Sparkles size={18} />,
      list: null,
      highlight: true,
    },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = t('reports.hub.createModal.errors.nameRequired');
    }

    if (!formData.year) {
      newErrors.year = t('reports.hub.createModal.errors.yearRequired');
    }

    if (!formData.type) {
      newErrors.type = t('reports.hub.createModal.errors.typeRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateReport = async () => {
    if (!validateForm()) {
      return;
    }

    setActiveAction('configurator');

    try {
      await onCreate({
        name: formData.name.trim(),
        year: parseInt(formData.year),
        type: formData.type,
      });

      // Reset form
      setFormData({
        name: '',
        year: new Date().getFullYear().toString(),
        type: '',
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error creating report:', error);
    } finally {
      setActiveAction(null);
    }
  };

  const createEmptyZipBlob = () => {
    const bytes = new Uint8Array(22);
    bytes[0] = 0x50;
    bytes[1] = 0x4b;
    bytes[2] = 0x05;
    bytes[3] = 0x06;
    return new Blob([bytes], { type: 'application/zip' });
  };

  const buildDownloadFilename = () => {
    const baseName = formData.name.trim() || t('reports.hub.createModal.downloadFilenameFallback');
    const sanitized = baseName.replace(/[^a-z0-9-_]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    return `${sanitized || BRAND.reportFilenameStem}-${formData.year}.zip`;
  };

  const handleDownloadPack = () => {
    if (!validateForm()) {
      return;
    }
    setActiveAction('download');
    try {
      const blob = createEmptyZipBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildDownloadFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast({
        title: t('common.success'),
        description: t('reports.hub.createModal.toast.downloaded'),
        variant: 'success',
      });
    } finally {
      setActiveAction(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isProcessing) {
      return;
    }
    if (selectedProcess === 'download') {
      handleDownloadPack();
      return;
    }
    await handleCreateReport();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="create-report-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="create-report-modal__title">
            {t('reports.hub.createModal.title')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="create-report-modal__form">
          <div className="create-report-modal__field">
            <label className="create-report-modal__label">
              {t('reports.hub.createModal.fields.name.label')}
            </label>
            <input
              type="text"
              className={`create-report-modal__input ${errors.name ? 'create-report-modal__input--error' : ''}`}
              placeholder={t('reports.hub.createModal.fields.name.placeholder')}
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              disabled={isProcessing}
            />
            {errors.name && (
              <span className="create-report-modal__error">{errors.name}</span>
            )}
          </div>

          <div className="create-report-modal__field">
            <label className="create-report-modal__label">
              {t('reports.hub.createModal.fields.year.label')}
            </label>
            <select
              className={`create-report-modal__select ${errors.year ? 'create-report-modal__input--error' : ''}`}
              value={formData.year}
              onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
              required
              disabled={isProcessing}
            >
              {years.map(year => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
            {errors.year && (
              <span className="create-report-modal__error">{errors.year}</span>
            )}
          </div>

          <div className="create-report-modal__field">
            <label className="create-report-modal__label">
              {t('reports.hub.createModal.fields.type.label')}
            </label>
            <div className="create-report-modal__radio-group">
              {reportTypeOptions.map(option => (
                <label
                  key={option.value}
                  className={`create-report-modal__radio-option ${
                    formData.type === option.value ? 'create-report-modal__radio-option--selected' : ''
                  } ${option.disabled ? 'create-report-modal__radio-option--disabled' : ''}`}
                >
                  <input
                    type="radio"
                    name="type"
                    value={option.value}
                    checked={formData.type === option.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                    className="create-report-modal__radio-input"
                    disabled={option.disabled || isProcessing}
                  />
                  <div className="create-report-modal__radio-content">
                    <div className="create-report-modal__radio-row">
                      <div className="create-report-modal__radio-label">
                        {option.label}
                      </div>
                      {option.badge && (
                        <span className="create-report-modal__radio-badge">
                          {option.badge}
                        </span>
                      )}
                    </div>
                    <div className="create-report-modal__radio-description">
                      {option.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            {errors.type && (
              <span className="create-report-modal__error">{errors.type}</span>
            )}
          </div>

          <div className="create-report-modal__section">
            <h3 className="create-report-modal__section-title">
              {t('reports.hub.createModal.process.title')}
            </h3>
            <div className="create-report-modal__process-grid">
              {processOptions.map((option) => (
                <label
                  key={option.value}
                  className={`create-report-modal__process-card ${
                    option.highlight ? 'create-report-modal__process-card--highlight' : ''
                  } ${selectedProcess === option.value ? 'create-report-modal__process-card--selected' : ''} ${
                    isProcessing ? 'create-report-modal__process-card--disabled' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="process"
                    value={option.value}
                    checked={selectedProcess === option.value}
                    onChange={(event) => setSelectedProcess(event.target.value)}
                    className="create-report-modal__process-input"
                    disabled={isProcessing}
                  />
                  <div className="create-report-modal__process-icon">
                    {option.icon}
                  </div>
                  <div className="create-report-modal__process-content">
                    <h4>{option.title}</h4>
                    <p>{option.description}</p>
                    {option.list ? (
                      <ul className="create-report-modal__process-list">
                        {option.list.map((item, index) => (
                          <li key={`${item}-${index}`}>{item}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </label>
              ))}
            </div>
            <p className="create-report-modal__process-note">
              {t('reports.hub.createModal.process.note')}
            </p>
          </div>

          <div className="create-report-modal__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isProcessing}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={primaryIcon}
              loading={isPrimaryLoading}
              disabled={!isFormComplete || isProcessing}
            >
              {primaryLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
